/* import __COLOCATED_TEMPLATE__ from './body.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import { hash } from 'ember-concurrency';
import {
  CHANNEL_METADATA_MAPPING,
  channelGroupMapping,
  CHANNEL_STANDALONE_API_CONVERSATION,
} from 'embercom/lib/operator/custom-bots/constants';

export default class ChannelTargeting extends Component {
  @service contentEditorService;
  @service store;
  @service appService;
  @service helpCenterService;

  constructor() {
    super(...arguments);
    this.loadChannelGroups.perform();
  }

  get hasMultipleHelpCenterSites() {
    return this.helpCenterService.sites?.length > 1;
  }

  get showMultiHcMessengerWarning() {
    return this.hasMultipleHelpCenterSites;
  }

  get emailSelected() {
    return this.contentEditorService.activeObject?.targetChannels?.includes('email');
  }

  get phoneSelected() {
    let channels = this.contentEditorService.activeObject?.targetChannels;

    return channels.length === 1 && channels.includes('phone_call');
  }

  get channelGroupTitle() {
    if (this.appService.app.canUseFeature('team-standalone-fin-over-api')) {
      return 'operator.workflows.channels.groupings.custom-built';
    }

    return this.appService.mainStandalonePlatform.componentData['channel-targeting-body']
      .channelGroupTitleKey;
  }

  targetChannelGroup(targetChannelGroup) {
    return channelGroupMapping(this.appService.app)
      [targetChannelGroup].filter((channel) => {
        return !this.args.config?.excludedChannels?.includes(channel);
      })
      .map((channel) => {
        return CHANNEL_METADATA_MAPPING[channel];
      });
  }

  @task({ drop: true })
  *loadChannelGroups() {
    this.channelGroup = yield hash({
      intercom: this.loadIntercomChannels,
      social: this.loadSocialChannels.perform(),
      other: this.loadOtherChannels.perform(),
    });

    if (this.appService.app.canUseStandalone) {
      // Copy over the Intercom Web channel from the original channel group
      if (
        this.appService.mainStandalonePlatform.componentData['channel-targeting-body']
          .addMessengerChannel
      ) {
        this.channelGroup.standaloneChannels = [
          {
            ...this.channelGroup.intercom[0],
            customLabelKey: 'standalone_intercom_web_messenger',
          },
        ];
      }

      this.channelGroup.standaloneChannels.pushObjects(
        this.appService.mainStandalonePlatform.componentData['channel-targeting-body'].channels,
      );

      if (this.appService.app.canUseFeature('team-standalone-fin-over-api')) {
        this.channelGroup.standaloneChannels.pushObject({
          value: CHANNEL_STANDALONE_API_CONVERSATION,
          icon: 'code',
          requiresAppStoreInstallation: false,
          channelTypeValue: 'StandaloneAPI',
          isSetup: true,
        });
      }
    }

    if (
      this.appService.app.canUseFinTasksAllChannels &&
      this.contentEditorService.activeObject.isAiAgentWorkflowTrigger
    ) {
      this.channelGroup.allChannels = [
        {
          value: 'all_channels',
          channelTypeValue: 'allChannels',
        },
      ];
    }

    /* eslint-enable @intercom/intercom/no-bare-strings */
  }

  get loadIntercomChannels() {
    return this.targetChannelGroup('intercom').map((channel) => {
      let isSetup = true;
      return {
        ...channel,
        isSetup,
      };
    });
  }

  @task({ drop: true })
  *loadSocialChannels() {
    let apps = yield this.store.query('appstore/app-package', { installed: true });
    return this.targetChannelGroup('social').map((channel) => {
      let isInstalled = apps.mapBy('id').includes(channel.appId);
      let isSetup = isInstalled;
      return {
        ...channel,
        isInstalled,
        isSetup,
        isDisabled: !isInstalled || !isSetup,
      };
    });
  }

  @task({ drop: true })
  *loadOtherChannels() {
    let smsOutboundSettings = yield this.store.findAll('sms-outbound-settings');
    let smsSetup = smsOutboundSettings?.firstObject?.twoWaySmsEnabled;
    let hasSlackChannelIntegration = false;
    if (this.appService.app.canUseSlackChannel) {
      let slackIntegrations = yield this.store.query('slack/integration', {
        app_id: this.appService.app.id,
      });
      hasSlackChannelIntegration = slackIntegrations.length > 0;
    }
    let hasDiscordChannelIntegration = false;
    if (this.appService.app.canUseDiscordChannel) {
      let discordIntegrations = yield this.store.query('discord/integration', {
        app_id: this.appService.app.id,
      });
      hasDiscordChannelIntegration = discordIntegrations.length > 0;
    }

    return this.targetChannelGroup('other').map((channel) => {
      let isSetup =
        channel.value === 'email' ||
        smsSetup ||
        channel.value === 'phone_call' ||
        (channel.value === 'slack' && hasSlackChannelIntegration) ||
        (channel.value === 'discord' && hasDiscordChannelIntegration);
      return {
        ...channel,
        isDisabled: !isSetup,
        isSetup,
      };
    });
  }
}
